import { useState, useMemo } from 'react'
import { Loader2, AlertTriangle, CheckCircle2, Search } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Slider } from '@/components/ui/slider'
import { usePartyDistrictStatesWithDistricts } from '@/hooks/usePartyDistrictStatesWithDistricts'
import { useCreateAction } from '@/hooks/useCreateAction'
import { estimateOrganizeEffect, getEfficiencyColor, formatEfficiency } from '@/lib/actionEffects'
import {
  ORGANIZE_EFFECTS,
  DISTRICT_SORT_OPTIONS,
  ORGANIZING_FOCUSES,
  FOCUS_ORDER,
  getOrganizationBarColor,
  type OrganizingFocus,
  type DistrictSortOption,
} from '@/constants/organize'
import { cn } from '@/lib/utils'
import type { PartyDistrictStateWithDistrict } from '@/types'

interface OrganizeActionFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  partyId: string
  currentTick: number
  availableAP: number
  onSuccess?: () => void
  onBack?: () => void
}

export function OrganizeActionForm({
  open,
  onOpenChange,
  partyId,
  currentTick,
  availableAP,
  onSuccess,
  onBack,
}: OrganizeActionFormProps) {
  if (!open) return null

  return (
    <OrganizeActionFormContent
      open={open}
      onOpenChange={onOpenChange}
      partyId={partyId}
      currentTick={currentTick}
      availableAP={availableAP}
      onSuccess={onSuccess}
      onBack={onBack}
    />
  )
}

function OrganizeActionFormContent({
  open,
  onOpenChange,
  partyId,
  currentTick,
  availableAP,
  onSuccess,
  onBack,
}: OrganizeActionFormProps) {
  // Form state
  const [selectedDistrictId, setSelectedDistrictId] = useState<string>('')
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [sortBy, setSortBy] = useState<DistrictSortOption>('organization_asc')
  const [intensity, setIntensity] = useState(ORGANIZE_EFFECTS.MIN_AP)
  const [focus, setFocus] = useState<OrganizingFocus | ''>('')

  // Fetch party's district states with district details
  const { districtStates, isLoading: districtsLoading } =
    usePartyDistrictStatesWithDistricts(partyId)

  // Create action mutation
  const { createAction, isCreating, error } = useCreateAction({
    onSuccess: () => {
      onSuccess?.()
      onOpenChange(false)
    },
  })

  // Filter and sort districts
  const filteredAndSortedDistricts = useMemo(() => {
    if (!districtStates) return []

    let filtered = [...districtStates]

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(
        (ds) =>
          ds.district.name.toLowerCase().includes(term) ||
          ds.district.region.name.toLowerCase().includes(term)
      )
    }

    // Apply sorting
    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'organization_asc':
          return a.organization_strength - b.organization_strength
        case 'organization_desc':
          return b.organization_strength - a.organization_strength
        case 'population':
          return b.district.population - a.district.population
        case 'recognition':
          return b.recognition - a.recognition
        case 'region': {
          const regionCompare = a.district.region.name.localeCompare(
            b.district.region.name
          )
          if (regionCompare !== 0) return regionCompare
          return a.district.name.localeCompare(b.district.name)
        }
        default:
          return 0
      }
    })
  }, [districtStates, searchTerm, sortBy])

  // Get selected district info
  const selectedDistrictState = useMemo(() => {
    if (!selectedDistrictId) return null
    return districtStates?.find((ds) => ds.district_id === selectedDistrictId)
  }, [selectedDistrictId, districtStates])

  // Current organization for effect estimate
  const currentOrganization = selectedDistrictState?.organization_strength ?? 0

  // Calculate estimated effects
  const effectEstimate = useMemo(() => {
    return estimateOrganizeEffect(intensity, currentOrganization, focus)
  }, [intensity, currentOrganization, focus])

  // Validation
  const canSubmit =
    !isCreating &&
    selectedDistrictId !== '' &&
    intensity <= availableAP &&
    availableAP >= ORGANIZE_EFFECTS.MIN_AP

  const apAfterAction = availableAP - intensity

  const handleSubmit = () => {
    if (!canSubmit) return

    createAction({
      party_id: partyId,
      tick: currentTick,
      action_type: 'organize',
      action_subtype: 'district_organizing',
      target_type: 'district',
      target_id: selectedDistrictId,
      action_points_spent: intensity,
      parameters: {
        focus: focus || undefined,
        district_name: selectedDistrictState?.district.name,
        region_id: selectedDistrictState?.district.region.id,
        current_organization: currentOrganization,
      },
    })
  }

  const handleBack = () => {
    onBack?.()
    onOpenChange(false)
  }

  const handleDistrictSelect = (districtId: string) => {
    setSelectedDistrictId(districtId)
  }

  const handleFocusToggle = (focusKey: OrganizingFocus) => {
    setFocus(focus === focusKey ? '' : focusKey)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>🗂️</span> Configure Organizing
          </DialogTitle>
          <DialogDescription>
            Build local volunteer networks and infrastructure to improve your
            ground game and election turnout.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* District Selector */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Target District</Label>
              <Select
                value={sortBy}
                onValueChange={(v) => setSortBy(v as DistrictSortOption)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Sort by..." />
                </SelectTrigger>
                <SelectContent>
                  {DISTRICT_SORT_OPTIONS.map((option) => (
                    <SelectItem key={option.key} value={option.key}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Search Input */}
            <div className="relative">
              <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
              <Input
                placeholder="Search districts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* District List */}
            {districtsLoading ? (
              <div className="flex h-48 items-center justify-center">
                <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
              </div>
            ) : filteredAndSortedDistricts.length === 0 ? (
              <div className="flex h-48 items-center justify-center rounded-lg border">
                <p className="text-muted-foreground text-sm">
                  {searchTerm ? 'No matching districts' : 'No districts found'}
                </p>
              </div>
            ) : (
              <div className="max-h-48 overflow-y-auto rounded-lg border">
                {filteredAndSortedDistricts.map((ds) => (
                  <DistrictRow
                    key={ds.district_id}
                    districtState={ds}
                    isSelected={selectedDistrictId === ds.district_id}
                    onSelect={() => handleDistrictSelect(ds.district_id)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Intensity Slider */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Intensity</Label>
              <span className="text-sm font-medium">{intensity} AP</span>
            </div>
            <Slider
              value={[intensity]}
              onValueChange={([value]) => setIntensity(value)}
              min={ORGANIZE_EFFECTS.MIN_AP}
              max={Math.min(ORGANIZE_EFFECTS.MAX_AP, availableAP)}
              step={1}
              disabled={availableAP < ORGANIZE_EFFECTS.MIN_AP}
            />
            <div className="text-muted-foreground flex justify-between text-xs">
              <span>2 - Standard (+8)</span>
              <span>3 - Intensive (+12)</span>
              <span>4 - Full Push (+15)</span>
            </div>
          </div>

          {/* Focus Selection */}
          <div className="space-y-3">
            <Label>
              Organizing Focus{' '}
              <span className="text-muted-foreground">(optional)</span>
            </Label>
            <div className="grid grid-cols-2 gap-2">
              {FOCUS_ORDER.map((focusKey) => (
                <FocusButton
                  key={focusKey}
                  focusKey={focusKey}
                  isSelected={focus === focusKey}
                  onClick={() => handleFocusToggle(focusKey)}
                  currentOrganization={currentOrganization}
                />
              ))}
            </div>
          </div>

          {/* Effect Preview */}
          {selectedDistrictId && (
            <div className="bg-muted/50 space-y-2 rounded-lg border p-4">
              <h4 className="text-sm font-medium">Expected Effect</h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Organization boost:
                  </span>
                  <span className="font-medium">
                    +{effectEstimate.totalGain} ({currentOrganization}% →{' '}
                    {effectEstimate.projectedOrganization}%)
                  </span>
                </div>
                {effectEstimate.focusBonus > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Focus bonus:</span>
                    <span className="font-medium text-green-600">
                      +{effectEstimate.focusBonus}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Efficiency:</span>
                  <span
                    className={cn(
                      'font-medium',
                      getEfficiencyColor(effectEstimate.efficiency)
                    )}
                  >
                    {formatEfficiency(effectEstimate.efficiency)}
                  </span>
                </div>
                <p className="text-muted-foreground pt-1 text-xs">
                  {effectEstimate.efficiencyReason}
                </p>
                <div className="border-t pt-2 mt-2">
                  <p className="text-xs">
                    <span className="text-muted-foreground">Result: </span>
                    {effectEstimate.benefitDescription}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Diminishing Returns Warning */}
          {selectedDistrictId && effectEstimate.showDiminishingWarning && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950">
              <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
              <div className="text-sm text-amber-700 dark:text-amber-300">
                <p className="font-medium">High Organization Warning</p>
                <p>
                  This district already has strong organization. Consider
                  targeting districts with lower organization for better
                  returns, or use Network Optimization focus.
                </p>
              </div>
            </div>
          )}

          {/* Insufficient AP Warning */}
          {availableAP < ORGANIZE_EFFECTS.MIN_AP && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950">
              <AlertTriangle className="h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />
              <div className="text-sm text-red-700 dark:text-red-300">
                <p className="font-medium">Insufficient Action Points</p>
                <p>
                  Organizing requires at least {ORGANIZE_EFFECTS.MIN_AP} AP. You
                  have {availableAP} AP available.
                </p>
              </div>
            </div>
          )}

          {/* AP Summary */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">AP after action:</span>
            <span
              className={cn('font-medium', apAfterAction < 0 && 'text-destructive')}
            >
              {availableAP} → {Math.max(0, apAfterAction)}
            </span>
          </div>

          {/* Error display */}
          {error && (
            <p className="text-destructive text-sm">
              {error.message || 'Failed to queue action'}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleBack}
            disabled={isCreating}
          >
            Back
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={!canSubmit}>
            {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isCreating ? 'Queuing...' : 'Queue Action'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ============================================================================
// Subcomponents
// ============================================================================

interface DistrictRowProps {
  districtState: PartyDistrictStateWithDistrict
  isSelected: boolean
  onSelect: () => void
}

function DistrictRow({ districtState, isSelected, onSelect }: DistrictRowProps) {
  const { district, recognition, organization_strength } = districtState

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'w-full border-b p-3 text-left transition-colors last:border-b-0',
        'hover:bg-accent/50',
        isSelected && 'bg-accent border-primary'
      )}
    >
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium">{district.name}</span>
            <span className="text-muted-foreground text-xs">
              ({district.region.name})
            </span>
          </div>
          <div className="text-muted-foreground mt-1 flex items-center gap-3 text-xs">
            <span className="capitalize">{district.settlement_type}</span>
            <span>Pop: {district.population.toLocaleString()}</span>
            <span>Recog: {recognition}%</span>
          </div>
        </div>
        {isSelected && (
          <CheckCircle2 className="text-primary h-4 w-4 shrink-0" />
        )}
      </div>
      {/* Organization Bar */}
      <div className="mt-2">
        <div className="mb-1 flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Organization</span>
          <span className="font-medium">{organization_strength}%</span>
        </div>
        <div className="bg-muted h-2 overflow-hidden rounded-full">
          <div
            className={cn(
              'h-full rounded-full transition-all',
              getOrganizationBarColor(organization_strength)
            )}
            style={{ width: `${organization_strength}%` }}
          />
        </div>
      </div>
    </button>
  )
}

interface FocusButtonProps {
  focusKey: OrganizingFocus
  isSelected: boolean
  onClick: () => void
  currentOrganization: number
}

function FocusButton({
  focusKey,
  isSelected,
  onClick,
  currentOrganization,
}: FocusButtonProps) {
  const config = ORGANIZING_FOCUSES[focusKey]
  const [minOrg, maxOrg] = config.bonusRange

  // Check if this focus would give a bonus at current organization level
  const wouldGiveBonus =
    focusKey === 'automatic'
      ? currentOrganization < 60
      : focusKey === 'network_optimization'
        ? currentOrganization > 60
        : currentOrganization >= minOrg && currentOrganization < maxOrg

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex flex-col items-start rounded-lg border p-3 text-left transition-colors',
        'hover:border-primary/50 hover:bg-accent/50',
        isSelected && 'border-primary bg-accent'
      )}
    >
      <div className="flex w-full items-center justify-between">
        <span className="text-lg">{config.icon}</span>
        {isSelected && <CheckCircle2 className="text-primary h-4 w-4" />}
      </div>
      <span className="mt-1 text-sm font-medium">{config.label}</span>
      <span className="text-muted-foreground mt-0.5 text-xs">
        {config.description}
      </span>
      {wouldGiveBonus && (
        <span className="mt-1 text-xs text-green-600 dark:text-green-400">
          {focusKey === 'network_optimization'
            ? 'Reduces diminishing returns'
            : '+3 bonus at current level'}
        </span>
      )}
    </button>
  )
}
